// src/pages/outsourcing_management/InquiryDetailsDisclosure.tsx
//
// The inquiry facts, as a labelled grid. Always shown open, in every state:
// the whole point of the grid is that a buyer can confirm what the inquiry
// says before acting on it, and hiding the fields (or the grid itself behind
// a toggle) hides the very thing they are checking.
//
// Every field is shown, empty ones as "-".
import { FunctionComponent, ReactNode } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { InquiryType } from "types/InquiryTypes";
import { reformatDate, reformatDateTime } from "util/DateUtil";
import { formatCentsOrDash } from "util/money";
import Fact from "components/Dashboard/Fact";
import { inquiryTypeLabel } from "./constants/inquiryStatusColors";

type PropsType = {
  inquiry: InquiryType;
};

// An unset deadline comes back as the epoch, which reformatDate renders as
// 01/01/1970. Same rule as the row (formatDeadline in OutsourcingRow).
const formatDate = (date: string | null | undefined) => {
  if (!date) return "-";
  const formatted = reformatDate(date);
  return formatted === "01/01/1970" ? "-" : formatted;
};

export const DetailsDisclosure: FunctionComponent<PropsType> = ({ inquiry }) => {
  const partnerName = `${inquiry.partner?.info?.firstName ?? ""} ${
    inquiry.partner?.info?.lastName ?? ""
  }`.trim();
  const facts: { key: string; label: string; node: ReactNode }[] = [
    { key: "title", label: "Title", node: inquiry.title || "-" },
    { key: "type", label: "Type", node: inquiryTypeLabel(inquiry.type) || "-" },
    {
      key: "supplier",
      label: "Final supplier",
      node: inquiry.partner?.info?.company ?? "-",
    },
    {
      key: "sent",
      label: "Sent",
      node: inquiry.sentAt ? reformatDateTime(inquiry.sentAt) : "-",
    },
    { key: "contact", label: "Contact", node: partnerName || "-" },
    {
      key: "deadline",
      label: "Deadline",
      node: formatDate(inquiry.deliveryDate),
    },
    {
      key: "method",
      label: "Delivery method",
      node: inquiry.deliveryMethod || "-",
    },
    { key: "raised", label: "Raised by", node: inquiry.created_by ?? "-" },
    // EE-253: set when the purchase order is placed; excl. VAT.
    {
      key: "items",
      label: "Items total",
      node: formatCentsOrDash(inquiry.itemsTotalCents),
    },
    {
      key: "transport",
      label: "Transport",
      node: formatCentsOrDash(inquiry.transportCostCents),
    },
    {
      key: "total",
      label: "Total excl. VAT",
      node: formatCentsOrDash(inquiry.totalCents),
    },
    {
      key: "deltime",
      label: "Delivery time",
      node: inquiry.deliveryTime || "-",
    },
    {
      key: "created",
      label: "Created",
      node: inquiry.created ? reformatDateTime(inquiry.created) : "-",
    },
  ];

  return (
    <Box>
      <Grid container spacing={1.5}>
        {facts.map((fact) => (
          <Fact key={fact.key} label={fact.label}>
            {fact.node}
          </Fact>
        ))}
        {inquiry.notes && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">
              Internal notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {inquiry.notes}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DetailsDisclosure;
